import type { HistoricalMethod, HistoricalPageResult } from './contract';
export const EXTERNAL_ROOT = '/normal-operations/risk-warning/evaluation/historical/external';
export const EXTERNAL_TRACKS = [
  'EXTERNAL_LOCAL_FIT_FROZEN_RECIPE_V1',
  'UAE_2024_FROZEN_DONOR_ZERO_SHOT_2025_V1',
] as const;
export type ExternalTrack = (typeof EXTERNAL_TRACKS)[number];
export interface ExternalCohort {
  stationId: string;
  name: string;
  country: string;
  latitude: number;
  longitude: number;
  climateContext: string;
  climateEvidence: string;
}
export interface ExternalSource {
  stationId: string;
  year: number;
  url: string;
  originalHash: string;
  bytes: number;
  rawRows: number;
  temperatureHours: number;
  windHours: number;
  temperatureCoverage: number;
  windCoverage: number;
  snapshotHash: string;
  qualityJson: string;
  accessedAt: string;
}
export interface ExternalRun {
  schemaVersion: string;
  runId: string;
  status: string;
  mode: string;
  createdAt: string;
  sourceType: string;
  license: string;
  doi: string;
  frozenInputSHA: string;
  preregistrationSHA: string;
  preregistrationHash: string;
  algorithmSHA: string;
  localFreezeSHA: string;
  donorFreezeSHA: string;
  sourceDatasetRevision: string;
  snapshotHash: string;
  rowsHash: string;
  groupsHash: string;
  daysHash: string;
  rowCount: number;
  groupCount: number;
  externalValidationUtility: string;
  transferStability: string;
  cohort: ExternalCohort[];
  sources: ExternalSource[];
  bounds: ExternalBounds;
  parametersJson: string;
  summaries: ExternalSummary[];
  limitations: string[];
}
export interface ExternalBounds {
  trainStart: string;
  trainEnd: string;
  validationStart: string;
  validationEnd: string;
  heldout2024Start: string;
  heldout2024End: string;
  heldout2025Start: string;
  heldout2025End: string;
  embargoHours: number;
}
export interface ExternalSummary {
  track: ExternalTrack;
  donorStationId: string | null;
  climateContext: string | null;
  testYear: number;
  metric: string;
  method: HistoricalMethod;
  macroGroups: number;
  pairedCount: number;
  microMAE: number | null;
  microRMSE: number | null;
  microBias: number | null;
  microBaselineMAE: number | null;
  microBaselineRMSE: number | null;
  macroMAE: number | null;
  macroRMSE: number | null;
  macroBaselineMAE: number | null;
  macroBaselineRMSE: number | null;
}
export interface ExternalReasonCount {
  reason: string | null;
  count: number;
}
export interface ExternalMonth {
  month: string;
  n: number;
  mae: number | null;
  rmse: number | null;
  bias: number | null;
  baselineMAE: number | null;
  baselineRMSE: number | null;
}
export interface ExternalInterval {
  status: string;
  blockDays: number;
  replicates: number;
  seed: number;
  effectiveDays: number;
  possibleBlocks: number;
  maeLow: number | null;
  maeHigh: number | null;
  rmseLow: number | null;
  rmseHigh: number | null;
}
export interface ExternalGroup {
  groupId: string;
  stationId: string;
  country: string;
  climateContext: string;
  testYear: number;
  track: ExternalTrack;
  donorStationId: string | null;
  metric: string;
  unit: string;
  horizonHours: number;
  method: HistoricalMethod;
  modelFreezeSHA: string;
  totalOrigins: number;
  generated: number;
  evaluable: number;
  pairedCount: number;
  coverage: number | null;
  mae: number | null;
  rmse: number | null;
  bias: number | null;
  pairedMAE: number | null;
  pairedRMSE: number | null;
  pairedBias: number | null;
  baselineMAE: number | null;
  baselineRMSE: number | null;
  baselineBias: number | null;
  baselineMAEDelta: number | null;
  baselineRMSEDelta: number | null;
  relativeMAEDelta: number | null;
  relativeRMSEDelta: number | null;
  eligibility: string;
  utility: string;
  interval: ExternalInterval;
  unavailableReasons: ExternalReasonCount[];
  months: ExternalMonth[];
}
export interface ExternalRow {
  groupId: string;
  stationId: string;
  climateContext: string;
  testYear: number;
  metric: string;
  unit: string;
  horizonHours: number;
  method: HistoricalMethod;
  track: ExternalTrack;
  donorStationId: string | null;
  modelFreezeSHA: string;
  sourceDatasetRevision: string;
  origin: string;
  targetAt: string;
  inputStart: string | null;
  inputEnd: string | null;
  inputRefs: string[];
  targetRefs: string[];
  predicted: number | null;
  actual: number | null;
  baselinePredicted: number | null;
  status: string;
  reason: string | null;
  paired: boolean;
}
export interface ExternalDay {
  groupId: string;
  day: string;
  n: number;
  absoluteErrorSum: number;
  squaredErrorSum: number;
  signedErrorSum: number;
  baselineAbsoluteErrorSum: number;
  baselineSquaredErrorSum: number;
  baselineSignedErrorSum: number;
}
export type ExternalPageResult<T> = HistoricalPageResult<T>;
export interface ExternalFilter {
  track: string;
  climateContext: string;
  country: string;
  stationId: string;
  testYear: string;
  metric: string;
  horizonHours: string;
  method: string;
  donorStationId: string;
  offset: number;
  limit: number;
}
export const INITIAL_EXTERNAL_FILTER: ExternalFilter = {
  track: EXTERNAL_TRACKS[0],
  climateContext: '',
  country: '',
  stationId: '',
  testYear: '2024',
  metric: 'TEMPERATURE',
  horizonHours: '1',
  method: '',
  donorStationId: '',
  offset: 0,
  limit: 12,
};
export interface ExternalLedgerFilter {
  groupId: string;
  from: string;
  to: string;
  offset: number;
  limit: number;
}
export interface ExternalPort {
  runs(
    offset: number,
    limit: number,
    signal: AbortSignal,
  ): Promise<ExternalPageResult<ExternalRun>>;
  run(id: string, signal: AbortSignal): Promise<ExternalRun>;
  groups(
    id: string,
    filter: ExternalFilter,
    signal: AbortSignal,
  ): Promise<ExternalPageResult<ExternalGroup>>;
  group(id: string, groupId: string, signal: AbortSignal): Promise<ExternalGroup>;
  rows(
    id: string,
    filter: ExternalLedgerFilter,
    signal: AbortSignal,
  ): Promise<ExternalPageResult<ExternalRow>>;
  errors(
    id: string,
    filter: ExternalLedgerFilter,
    signal: AbortSignal,
  ): Promise<ExternalPageResult<ExternalRow>>;
  days(
    id: string,
    filter: ExternalLedgerFilter,
    signal: AbortSignal,
  ): Promise<ExternalPageResult<ExternalDay>>;
}
export const validExternalRunId = (id: string) => /^external-[a-f0-9]{64}$/.test(id);
export const validExternalGroupId = (id: string) => /^g-[a-f0-9]{64}$/.test(id);
export function externalHref(
  id = '',
  filter = INITIAL_EXTERNAL_FILTER,
  areaId = '',
  groupId = '',
): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(filter)) if (v !== '' && k !== 'limit') p.set(k, String(v));
  if (areaId) p.set('areaId', areaId);
  return `${EXTERNAL_ROOT}${id ? `/runs/${id}` : ''}${groupId ? `/groups/${groupId}` : ''}${p.size ? `?${p}` : ''}`;
}
export function parseExternalLocation(path: string, search: string) {
  const p = new URLSearchParams(search);
  const filter = { ...INITIAL_EXTERNAL_FILTER };
  let valid = true;
  const allowed = [...Object.keys(filter).filter((k) => k !== 'limit'), 'areaId'];
  for (const k of p.keys()) if (!allowed.includes(k) || p.getAll(k).length !== 1) valid = false;
  for (const k of [
    'track',
    'climateContext',
    'country',
    'stationId',
    'testYear',
    'metric',
    'horizonHours',
    'method',
    'donorStationId',
  ] as const)
    if (p.has(k)) filter[k] = p.get(k) ?? '';
  filter.offset = Number(p.get('offset') ?? 0);
  const suffix = path.replace(/\/$/, '').slice(EXTERNAL_ROOT.length);
  const m = /^\/runs\/(external-[a-f0-9]{64})(?:\/groups\/(g-[a-f0-9]{64}))?$/.exec(suffix);
  const areaId = p.get('areaId') ?? '';
  return {
    id: m?.[1] ?? null,
    groupId: m?.[2] ?? null,
    filter,
    areaId,
    valid:
      valid &&
      path.startsWith(EXTERNAL_ROOT) &&
      (suffix === '' || Boolean(m)) &&
      (!areaId || /^[a-z][a-z0-9-]{0,63}$/.test(areaId)),
  };
}
export function externalTrackLabel(track: string): string {
  return track === EXTERNAL_TRACKS[0]
    ? '当地训练 · 冻结算法'
    : track === EXTERNAL_TRACKS[1]
      ? '原 UAE 模型 · 零样本迁移'
      : track;
}
